import { useState } from "react";
import { createPortal } from "react-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Copy, DownloadSimple, Paperclip, Play, X, ArrowsClockwise, FileDoc, FilePpt, FilePdf, CircleNotch } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, assetUrl, copyToClipboard, errDetail, MODEL_META, modelLabel, getToken, BACKEND_URL } from "../lib/api";
import { AttachmentCard } from "./AttachmentCard";
import { ImagePreview } from "./ImagePreview";

const copyText = async (content) => {
  const ok = await copyToClipboard(content);
  if (ok) toast.success("Copied to clipboard");
  else toast.error("Could not copy — select the text manually");
};

const EXT_MAP = { html: "html", javascript: "js", js: "js", jsx: "jsx", typescript: "ts", tsx: "tsx", python: "py", css: "css", json: "json", markdown: "md", bash: "sh", shell: "sh", sql: "sql", java: "java", cpp: "cpp", c: "c", xml: "xml", yaml: "yml" };

const extractCode = (node) => {
  if (node == null) return "";
  if (typeof node === "string") return node;
  if (Array.isArray(node)) return node.map(extractCode).join("");
  return extractCode(node.props?.children);
};

const HtmlPreview = ({ code, onClose }) =>
  createPortal(
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8 scale-in"
      data-testid="html-preview-modal" onClick={onClose}>
      <div className="absolute top-4 right-4 z-10">
        <button onClick={onClose} data-testid="html-preview-close" title="Close"
          className="text-white/80 hover:text-white bg-white/10 hover:bg-white/20 p-2.5 rounded-full transition-colors duration-200"><X size={20} /></button>
      </div>
      <div className="w-full max-w-5xl h-[88vh] rounded-xl overflow-hidden bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <iframe title="Live preview" srcDoc={code} sandbox="allow-scripts" className="w-full h-full border-0" />
      </div>
    </div>,
    document.body
  );

const CodePre = ({ children, ...props }) => {
  const [showPreview, setShowPreview] = useState(false);
  const codeEl = Array.isArray(children) ? children[0] : children;
  const cls = codeEl?.props?.className || "";
  const lang = (cls.match(/language-([\w-]+)/) || [])[1] || "text";
  const text = extractCode(codeEl);
  const ext = EXT_MAP[lang] || "txt";
  const previewable = ["html", "svg", "xml"].includes(lang) || text.trim().startsWith("<svg") || text.trim().startsWith("<!DOCTYPE") || text.trim().startsWith("<html");
  const download = () => {
    const blob = new Blob([text], { type: "text/plain" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `triad-code.${previewable && ext === "txt" ? "html" : ext}`;
    a.click();
    URL.revokeObjectURL(a.href);
  };
  return (
    <div className="rounded-xl overflow-hidden border border-[var(--border)] my-3">
      <div className="flex items-center justify-between bg-[var(--surface-3)] px-3.5 py-1.5">
        <span className="text-[11px] font-medium text-[var(--text-2)] uppercase">{lang}</span>
        <span className="flex gap-0.5 items-center">
          {previewable && (
            <button data-testid="preview-code-button" onClick={() => setShowPreview(true)} title="Live preview"
              className="flex items-center gap-1 text-[11px] font-semibold text-[var(--accent)] hover:opacity-80 px-2 py-1 rounded-md transition-opacity duration-300">
              <Play size={12} weight="fill" /> Preview
            </button>
          )}
          <button data-testid="copy-code-button" onClick={() => copyText(text)} title="Copy code"
            className="text-[var(--muted)] hover:text-[var(--text)] p-1.5 rounded-md transition-colors duration-300"><Copy size={13} /></button>
          <button data-testid="download-code-button" onClick={download} title={`Download as file`}
            className="text-[var(--muted)] hover:text-[var(--text)] p-1.5 rounded-md transition-colors duration-300"><DownloadSimple size={13} /></button>
        </span>
      </div>
      <pre {...props} style={{ margin: 0, borderRadius: 0, border: "none" }}>{children}</pre>
      {showPreview && <HtmlPreview code={text} onClose={() => setShowPreview(false)} />}
    </div>
  );
};

const Attachments = ({ list }) => {
  if (!list?.length) return null;
  return (
    <div className="flex flex-wrap gap-2 mb-2">
      {list.map((a, i) =>
        typeof a === "string" ? (
          <span key={i} className="inline-flex items-center gap-1 text-[11px] text-[var(--text-2)] bg-[var(--surface)] border border-[var(--border)] rounded-full px-2.5 py-1">
            <Paperclip size={10} /> {a}
          </span>
        ) : (
          <AttachmentCard key={a.id || i} att={a} compact />
        )
      )}
    </div>
  );
};

const guessTitle = (content) => {
  const first = (content || "").split("\n").find((l) => l.trim());
  if (!first) return "";
  return first.replace(/^#+\s+/, "").replace(/\*+/g, "").slice(0, 80).trim();
};

const ExportMenu = ({ content, title }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null); // "docx" | "pptx" | "pdf" | null
  const run = async (fmt) => {
    setBusy(fmt);
    setOpen(false);
    try {
      const res = await fetch(`${BACKEND_URL}/api/export-doc`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ content, format: fmt, title }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const filename = decodeURIComponent(
        (res.headers.get("content-disposition") || "").match(/filename="?([^"]+)"?/)?.[1]
        || `trika-export.${fmt}`
      );
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(`Exported as .${fmt}`);
    } catch (e) {
      toast.error(errDetail(e));
    } finally {
      setBusy(null);
    }
  };
  const items = [
    { fmt: "docx", label: "Word (.docx)", Icon: FileDoc },
    { fmt: "pptx", label: "PowerPoint (.pptx)", Icon: FilePpt },
    { fmt: "pdf", label: "PDF (.pdf)", Icon: FilePdf },
  ];
  return (
    <span className="relative">
      <button data-testid="export-message-button" onClick={() => setOpen((o) => !o)} title="Export as Word / PowerPoint / PDF"
        className="text-[var(--muted)] hover:text-[var(--text)] hover:bg-[var(--hover)] transition-colors duration-300 p-1.5 rounded-lg disabled:opacity-50"
        disabled={busy !== null}>
        {busy ? <CircleNotch size={14} className="animate-spin" /> : <FileDoc size={14} />}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40 cursor-default" onClick={() => setOpen(false)} data-testid="export-menu-backdrop" />
          <div className="absolute right-0 top-full mt-1 z-50 min-w-[180px] rounded-xl overflow-hidden border border-[var(--border-strong)] bg-[var(--surface)] shadow-lg" data-testid="export-menu-list">
            {items.map(({ fmt, label, Icon }) => (
              <button key={fmt} data-testid={`export-${fmt}`} onClick={() => run(fmt)}
                className="w-full text-left px-3.5 py-2 text-[13px] text-[var(--text)] hover:bg-[var(--hover)] flex items-center gap-2.5 transition-colors duration-200">
                <Icon size={16} weight="duotone" style={{ color: "var(--accent)" }} /> {label}
              </button>
            ))}
          </div>
        </>
      )}
    </span>
  );
};

export const MessageBlock = ({ msg, streaming, onRetry, exportIntent }) => {
  const [showImage, setShowImage] = useState(false);
  const [showRaw, setShowRaw] = useState(false);
  const [exporting, setExporting] = useState(false);
  // exportIntent: "pptx" | "docx" | "pdf" | null — the previous user message asked for a doc/deck
  const shouldShowExportCard = !streaming && !!exportIntent && !!msg.content && !msg.asset_id;

  const runInlineExport = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/export-doc`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ content: msg.content, format: exportIntent, title: guessTitle(msg.content) }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const filename = decodeURIComponent(
        (res.headers.get("content-disposition") || "").match(/filename="?([^"]+)"?/)?.[1]
        || `trika-export.${exportIntent}`
      );
      const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = filename; a.click();
      URL.revokeObjectURL(a.href);
      toast.success(`Downloaded ${filename}`);
    } catch (e) {
      toast.error(errDetail(e));
    } finally {
      setExporting(false);
    }
  };

  if (msg.role === "user") {
    return (
      <div className="flex justify-end fade-up group" data-testid={`message-user-${msg.id}`}>
        <div className="max-w-[75%]">
          <div className="bg-[var(--surface-2)] rounded-3xl rounded-br-lg px-5 py-3">
            <Attachments list={msg.attachments} />
            <p className="text-[15px] whitespace-pre-wrap break-words [overflow-wrap:anywhere] text-[var(--text)]">{msg.content}</p>
          </div>
          <div className="flex justify-end mt-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <button data-testid="copy-user-message-button" onClick={() => copyText(msg.content)} title="Copy"
              className="text-[var(--muted)] hover:text-[var(--text)] hover:bg-[var(--hover)] transition-colors duration-300 p-1.5 rounded-lg"><Copy size={13} /></button>
          </div>
        </div>
      </div>
    );
  }

  const meta = MODEL_META[msg.provider] || MODEL_META.openai;
  const download = () => {
    const blob = new Blob([msg.content], { type: "text/markdown" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `triad-${(msg.model || "output")}-${msg.id?.slice(0, 6) || "doc"}.md`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="fade-up group" data-testid={`message-assistant-${msg.id || "streaming"}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[10px] font-bold" style={{ background: meta.color }}>
          {meta.label[0]}
        </span>
        <span className="text-xs font-semibold text-[var(--text)]">{modelLabel(msg.model) || meta.label}</span>
        {msg.stopped && <span className="text-[10px] text-[var(--muted)] italic">stopped</span>}
        {!streaming && (msg.content || msg.asset_id) && (
          <span className="flex gap-0.5 ml-auto opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            {onRetry && (
              <button data-testid="retry-message-button" onClick={() => onRetry(msg)} title="Retry with same model"
                className="text-[var(--muted)] hover:text-[var(--accent)] hover:bg-[var(--hover)] transition-colors duration-300 p-1.5 rounded-lg"><ArrowsClockwise size={14} /></button>
            )}
            <button data-testid="copy-message-button" onClick={() => copyText(msg.content)} title="Copy"
              className="text-[var(--muted)] hover:text-[var(--text)] hover:bg-[var(--hover)] transition-colors duration-300 p-1.5 rounded-lg"><Copy size={14} /></button>
            {msg.content && !msg.asset_id && <ExportMenu content={msg.content} title={guessTitle(msg.content)} />}
            <button data-testid="download-message-button" onClick={download} title="Download as .md"
              className="text-[var(--muted)] hover:text-[var(--text)] hover:bg-[var(--hover)] transition-colors duration-300 p-1.5 rounded-lg"><DownloadSimple size={14} /></button>
          </span>
        )}
      </div>
      <div className="pl-8">
        {msg.asset_id && (
          <>
            <img src={assetUrl(msg.asset_id)} alt={msg.content || "generated"} data-testid="generated-image"
              onClick={() => setShowImage(true)} title="Click to preview"
              className="max-w-md w-full rounded-2xl border border-[var(--border)] mb-3 cursor-pointer hover:opacity-90 transition-opacity duration-300" />
            {showImage && <ImagePreview url={assetUrl(msg.asset_id)} name={`triad-${msg.asset_id.slice(0, 6)}`} onClose={() => setShowImage(false)} sourceType="asset" sourceId={msg.asset_id} />}
          </>
        )}
        <div className="markdown-body text-[var(--text)]">
          {shouldShowExportCard && !showRaw ? (
            <div className="rounded-2xl border border-[var(--border-strong)] bg-[var(--surface)] p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4" data-testid="inline-export-card">
              <span className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
                style={{ background: "var(--accent)", color: "white" }}>
                {exportIntent === "pptx" ? <FilePpt size={22} weight="fill" />
                  : exportIntent === "docx" ? <FileDoc size={22} weight="fill" />
                  : <FilePdf size={22} weight="fill" />}
              </span>
              <div className="flex-1 min-w-0">
                <p className="font-serif-display font-semibold text-[15px] text-[var(--text)] truncate">
                  {exportIntent === "pptx" ? "Presentation ready" : exportIntent === "docx" ? "Document ready" : "PDF ready"}
                </p>
                <p className="text-[12px] text-[var(--text-2)] truncate">{guessTitle(msg.content) || "Untitled"}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button data-testid="inline-export-download" onClick={runInlineExport} disabled={exporting}
                  className="inline-flex items-center gap-1.5 rounded-full text-white font-semibold text-[13px] px-4 py-2 transition-opacity duration-300 disabled:opacity-50"
                  style={{ background: "var(--accent)" }}>
                  {exporting ? <CircleNotch size={14} className="animate-spin" /> : <DownloadSimple size={14} weight="bold" />}
                  {exporting ? "Preparing…" : `Download .${exportIntent}`}
                </button>
                <button data-testid="inline-export-show-text" onClick={() => setShowRaw(true)}
                  className="text-[12px] font-medium text-[var(--muted)] hover:text-[var(--text)] underline underline-offset-4 decoration-dotted">
                  Show text
                </button>
              </div>
            </div>
          ) : (
            <>
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ pre: CodePre }}>{msg.content}</ReactMarkdown>
              {streaming && <span className="cursor-blink inline-block w-2 h-4 rounded-sm align-middle ml-1" style={{ background: "var(--accent)" }} />}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
